import { Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { db } from "./services/firebase";
import { onAuthChange, User } from "./services/auth.service";
import { doc, getDoc } from "firebase/firestore";
import CookieConsent from "./components/CookieConsent";

import LoginPage from "./pages/LoginPage";
import Home from "./pages/Home";
import LandingPage from "./pages/LandingPage";
import ProtectedRoute from "./components/ProtectedRoute";
import TutorialSwiper from "./components/tutorial/tutorial";
import SetupPane from "./components/setup/setup";
import ConsentGate from "./components/Consentgate";
import { FONT_MONO } from "theme/fonts";

type AppInnerProps = {
  user: User | null;
  forceReset: boolean;
};

export default function App() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [forceReset, setForceReset] = useState<boolean>(false);

  useEffect(() => {
    const unsub = onAuthChange(async (u: User | null) => {
      if (u) {
        try {
          await u.getIdToken(true);
          const snap = await getDoc(doc(db, "users", u.uid));
          setForceReset(snap.exists() && snap.data().forcePasswordReset === true);
        } catch (err) {
          console.error("Firestore check failed:", err);
          setForceReset(false);
        }
      } else {
        setForceReset(false);
      }
      setUser(u ?? null);
    });
    return () => unsub();
  }, []);

  if (user === undefined) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", background: "#050b18",
        color: "#38bdf8", fontFamily: FONT_MONO, fontSize: 13,
      }}>
        Initializing...
      </div>
    );
  }

  return <AppInner user={user} forceReset={forceReset} />;
}

function AppInner({ user, forceReset }: AppInnerProps) {
  return (
    <>
      <Routes>
        {/* ROOT */}
        <Route path="/" element={<Navigate to="/home" replace />} />

        <Route
          path="/login"
          element={
            user ? (
              forceReset ? <LoginPage forceReset={true} /> : <Navigate to="/session" replace />
            ) : (
              <ConsentGate>
                <LoginPage />
              </ConsentGate>
            )
          }
        />

        <Route
          path="/home"
          element={user ? <Navigate to="/session" replace /> : <TutorialSwiper />}
        />

        <Route
          path="/session"
          element={user ? <Home user={user} /> : <Navigate to="/home" replace />}
        />

        <Route path="*" element={<Navigate to="/home" replace />} />
      </Routes>

      <CookieConsent />
    </>
  );
}